import React from "react";
import { Box } from "@mui/material";
import {
  certificatePageSize,
  certificatePaperColour,
} from "../../../domain/certificates/certificateTemplates";
import { CentredLayout, type CertificateDesign } from "./certificateParts";
import { pointsAround, starPoints } from "./ornamentShapes";
import { Trophy } from "./Trophy";

/** The rainbow's stripes, in order. */
const RAINBOW = ["#e8384f", "#f68b2c", "#f7d23e", "#5cc35c", "#2d9cdb", "#8f5bd6"];

/** The cloud panel's straight edge, this far in from the page edge; its puffs reach out past it. */
const CLOUD_INSET = 72;
const PUFF_RADIUS = 30;
const PUFF_SPACING = 52;

/** Diagonal rainbow stripes over the whole page, with a few bubbles. */
const RainbowBackground: React.FC<{ width: number; height: number }> = ({
  width,
  height,
}) => {
  const diagonal = Math.hypot(width, height);
  const stripe = diagonal / RAINBOW.length;
  return (
    <>
      <g transform={`rotate(-35 ${width / 2} ${height / 2})`}>
        {RAINBOW.map((colour, i) => (
          <rect
            key={colour}
            x={width / 2 - diagonal / 2 + stripe * i}
            y={height / 2 - diagonal / 2}
            width={stripe + 1}
            height={diagonal}
            fill={colour}
          />
        ))}
      </g>
      {[
        [0.04, 0.5, 16],
        [0.96, 0.24, 20],
        [0.92, 0.9, 14],
        [0.3, 0.97, 12],
        [0.62, 0.03, 13],
      ].map(([x, y, r]) => (
        <circle
          key={`${x},${y}`}
          cx={width * x}
          cy={height * y}
          r={r}
          fill="#ffffff"
          fillOpacity={0.25}
          stroke="#ffffff"
          strokeOpacity={0.8}
          strokeWidth={2}
        />
      ))}
    </>
  );
};

/** Stars round the edge of the page, outside the cloud, in turns of size. */
const EdgeStars: React.FC<{ width: number; height: number; colour: string }> = ({
  width,
  height,
  colour,
}) => (
  <>
    {pointsAround(width, height, 24, 96).map((point, i) => {
      const size = [18, 11, 24, 14][i % 4];
      return (
        <polygon
          key={i}
          points={starPoints(5, size, size * 0.45)}
          transform={`translate(${point.x} ${point.y}) rotate(${(i * 23) % 40 - 20})`}
          fill={colour}
          stroke="#ffffff"
          strokeWidth={2}
          strokeLinejoin="round"
        />
      );
    })}
  </>
);

/**
 * Rainbow: a puffy white cloud on rainbow stripes, ringed with gold stars,
 * with a trophy above the signatures, like a bright primary school
 * certificate of achievement.
 */
export const rainbowDesign: CertificateDesign = {
  bodyFont: "nunito",
  Layout: ({ template, text, orientation }) => {
    const { primaryColour: primary, accentColour: accent } = template;
    const { width, height } = certificatePageSize(orientation);
    const paper = certificatePaperColour(template);
    return (
      <CentredLayout
        template={template}
        text={text}
        orientation={orientation}
        lettering={{ achievementColour: accent }}
        inset={{
          landscape: "84px 120px 84px",
          portrait: "100px 108px 96px",
        }}
        centrepiece={<Trophy colour={primary} />}
        frame={
          <Box
            component="svg"
            viewBox={`0 0 ${width} ${height}`}
            aria-hidden="true"
            sx={{ position: "absolute", inset: 0, width, height }}
          >
            <RainbowBackground width={width} height={height} />
            <EdgeStars width={width} height={height} colour={primary} />
            {/* The cloud: a panel with a puff all along its edge. */}
            <g fill={paper}>
              <rect
                x={CLOUD_INSET}
                y={CLOUD_INSET}
                width={width - CLOUD_INSET * 2}
                height={height - CLOUD_INSET * 2}
              />
              {pointsAround(width, height, CLOUD_INSET, PUFF_SPACING).map(
                ({ x, y }) => (
                  <circle key={`${x},${y}`} cx={x} cy={y} r={PUFF_RADIUS} />
                ),
              )}
            </g>
          </Box>
        }
      />
    );
  },
};
